import _ from 'lodash';
import React from 'react';
import { navigateAction } from 'fluxible-router';
import shallowCompare from '../../../lib/shallowCompare';
import Customer from '../../../models/Customer';
import Store from '../../../models/Store';
import { RequestStates } from '../../../stores/PaginatedStore';
import CustomerActions from '../../../actions/CustomerActions';
import InfoCircle from '../../InfoCircle';
import AuthModal from '../../AuthModal';
import styles from './index.sass';
import banquetFooterLogo from '../../Footer/images/banquet_footer_logo_3x.png';

class ContactInfo extends React.Component {
  static contextTypes = {
    executeAction : React.PropTypes.func,
    buildURLForRoute: React.PropTypes.func
  };

  static propTypes = {
    customer: React.PropTypes.instanceOf(Customer),
    store: React.PropTypes.instanceOf(Store),
    error: React.PropTypes.object,
    requestStatus: React.PropTypes.string,
    onChange: React.PropTypes.func
  };

  constructor(props) {
    super(props);
    this.state = _.extend(this.getStateFromProps(props), {
      showPasswordInput: false,
      showAuthModal: false,
      pendingAuth: false,
      emailPasswordSubmitted: false,
      invalidFields: {}
    });
  }

  getContactInfoHash() {
    return {
      email: this.state.email || '',
      name: this.state.name || '',
      phone: this.state.phone || ''
    };
  }

  getStateFromProps(props) {
    let newState = {
      password: null,
      loading: false, // Reset with updated customer entity
      error: null
    };
    if(!(this.state && this.state.email) || !this.props.customer || props.customer.email !== this.props.customer.email) {
      newState.email = props.customer.email;
    }
    if(!(this.state && this.state.name) || !this.props.customer || props.customer.name !== this.props.customer.name) {
      newState.name = props.customer.name;
    }
    if(!(this.state && this.state.phone) || !this.props.customer || props.customer.phone !== this.props.customer.phone) {
      newState.phone = props.customer.phone;
    }
    return newState;
  }

  componentWillReceiveProps(nextProps) {
    if(this.props.customer !== nextProps.customer) {
      this.setState(this.getStateFromProps(nextProps));
    }
    if(this.props.error !== nextProps.error) {
      if(this.state.pendingAuth && nextProps.error) {
        this.setState({
          pendingAuth: false,
          error: null,
          loading: false,
          invalidFields: {}
        });
        if(nextProps.error.name === 'ParametersInvalidError') {
          this.setState({showPasswordInput: true});
        }
      } else if (!this.state.showPasswordInput && nextProps.error && nextProps.error.name === 'ParametersInvalidError') {
        this.setState({
          error: null
        });
      } else {
        let newState = {
          error: nextProps.error,
          emailPasswordSubmitted: false,
          loading: false,
          invalidFields: {}
        };
        if(nextProps.error && nextProps.error.params) {
          _.each(nextProps.error.params, (obj) => {
            newState.invalidFields[obj.key] = obj.message;
          });
        }
        this.setState(newState);
      }
    }
    if(this.props.requestStatus !== nextProps.requestStatus) {
      this.setState({pendingAuth: false});
      if(this.state.emailPasswordSubmitted && this.state.showPasswordInput && nextProps.requestStatus === RequestStates.Done) {
        this.setState({
          error: {message: `The email and password you entered don't match.`},
          loading: false,
          invalidFields: {}
        });
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }

  handleOnBlurContactInfoField(field, e) {
    if(this.props.customer.isSignedIn()) {
      this.context.executeAction(CustomerActions.updateContactInfo, {
        [field]: e.target.value
      });
    }
  }

  handleOnChangeName(e) {
    this.setState({name: e.target.value});
    if(this.state.invalidFields.name) {
      this.setState({
        invalidFields: _.omit(this.state.invalidFields, 'name'),
        error: null
      });
    }
  }

  handleOnChangeEmail(e) {
    let email = e.target.value;
    this.setState({email});
    if(email.match(/.@./) && !this.state.showPasswordInput) {
      this.setState({pendingAuth: true});
      this.context.executeAction(CustomerActions.auth, {
        email: email,
        addIdentifierIfNotFound: false
      });
    }
    if(this.state.invalidFields.email) {
      this.setState({
        invalidFields: _.omit(this.state.invalidFields, 'email'),
        error: null
      });
    }
  }

  handleOnChangePhone(e) {
    let value = e.target.value.replace(/[^0-9\-]/g, '');
    this.setState({phone: value});
    if(this.state.invalidFields.phone) {
      this.setState({
        invalidFields: _.omit(this.state.invalidFields, 'phone'),
        error: null
      });
    }
  }

  handleOnChangePassword(e) {
    this.setState({password: e.target.value});
  }

  handleOnClickSignOut(e) {
    let signOutUrl = this.context.buildURLForRoute('signOut');
    this.context.executeAction(navigateAction, {url: signOutUrl});
  }

  handleOnClickSignIn(e) {
    let { email, password } = this.state;
    if(!(email && email.length) || !(password && password.length)) {return;}
    this.setState({loading: true, emailPasswordSubmitted: true});
    this.context.executeAction(CustomerActions.auth, {email, password});
  }

  handleOnClickPasswordFieldToggle(e) {
    this.setState({showPasswordInput: true});
  }

  handleOnClickForgotPassword(e) {
    this.setState({showAuthModal: true});
  }

  handleOnKeyDownPassword(e) {
    let { email, password } = this.state;
    if(!(email && email.length) || !(password && password.length)) {return;}
    if(e.which === 13) {
      this.handleOnClickSignIn();
    }
  }

  handleOnCloseAuthModal() {
    this.setState({showAuthModal: false});
  }

  renderError() {
    let {error} = this.state;
    if(!error) {return null;} // Only show one-type at at a time. Default to customerError (handled by ContactInfo)
    let message;
    if(error.params) {
      // Only show first
      message = error.params[0].message;
    } else {
      message = error.user_message || error.message;
    }
    return <p className={styles.error}>{message}</p>;
  }

  renderPasswordSection() {
    let { customer } = this.props;
    if(customer.isSignedIn()) {
      let signOutProps = {onClick: this.handleOnClickSignOut.bind(this)};
      return <p className={styles.subtext}>Not you? <button {...signOutProps}>Sign out</button>.</p>;
    }
    if(!this.state.showPasswordInput) {
      let passwordToggleProps = {
        onClick: this.handleOnClickPasswordFieldToggle.bind(this),
        tabIndex: -1
      };
      return <p className={styles.subtext}>Already have an acount? <button {...passwordToggleProps}>Sign in</button>.</p>;
    }
    let { email, password } = this.state;
    let passwordInputProps = {
      className: styles.passwordInput,
      type: 'password',
      placeholder: 'Enter password',
      value: password,
      onChange: this.handleOnChangePassword.bind(this),
      onKeyDown: this.handleOnKeyDownPassword.bind(this)
    };
    let signInButtonProps = {
      className: styles.signInButton,
      disabled: !(password && password.length && email && email.length) || this.state.loading,
      onClick: this.handleOnClickSignIn.bind(this)
    };
    let signInButtonContent = this.state.loading ? <span className="loading-spinner--white" /> : 'Sign in';
    let forgotPasswordProps = {
      className: styles.forgotPasswordButton,
      onClick: this.handleOnClickForgotPassword.bind(this)
    };

    return (
      <div>
        <div className={styles.passwordWrapper}>
          <input {...passwordInputProps} />
          <button {...signInButtonProps}>{signInButtonContent}</button>
        </div>
        <p className={styles.subtext}><button {...forgotPasswordProps}>Forgot password?</button></p>
        <div className={styles.poweredBy}>
          <span>Powered by</span>
          <img className={styles.banquetLogo} src={banquetFooterLogo} />
          <InfoCircle text="Sign in with Banquet to securely check out on this and other sites." />
        </div>
      </div>
    );
  }

  renderNameInput(invalid) {
    let { name } = this.state;
    let nameInputProps = {
      className   : styles.nameInput + (invalid ? ` ${styles.invalid}` : ''),
      type        : 'text',
      autoComplete: 'name',
      placeholder : 'Full name',
      value       : name,
      onChange    : this.handleOnChangeName.bind(this),
      onBlur      : this.handleOnBlurContactInfoField.bind(this, 'name')
    };
    return <input {...nameInputProps} />;
  }

  renderPhoneInput(invalid) {
    let { phone } = this.state;
    let phoneInputProps = {
      className   : styles.phoneInput + (invalid ? ` ${styles.invalid}` : ''),
      type        : 'text',
      autoComplete: 'tel',
      placeholder : 'Phone number',
      value       : phone,
      onChange    : this.handleOnChangePhone.bind(this),
      onBlur      : this.handleOnBlurContactInfoField.bind(this, 'phone')
    };
    return <input {...phoneInputProps} />;
  }

  renderEmailSection(invalid) {
    let { customer } = this.props;
    let { email } = this.state;
    if(customer.isSignedIn()) {
      return <p className={styles.email}>{email}</p>;
    }
    let emailInputProps = {
      className   : styles.emailInput + (invalid ? ` ${styles.invalid}` : ''),
      type        : 'email',
      placeholder : 'Email address',
      value       : email,
      onChange    : this.handleOnChangeEmail.bind(this),
      onBlur      : this.handleOnBlurContactInfoField.bind(this, 'email')
    };
    return <input {...emailInputProps} />;
  }

  renderAuthModal() {
    if(!this.state.showAuthModal) {return false;}
    let authModalProps = {
      onClose: this.handleOnCloseAuthModal.bind(this),
      mode: 'PasswordReset',
      email: this.state.email
    };
    return <AuthModal {...authModalProps} />;
  }

  render() {
    let { customer, error } = this.props;
    let { invalidFields } = this.state;

    return (
      <div className={styles.contactInfo}>
        {this.renderError()}
        {this.renderEmailSection(invalidFields.email)}
        {this.renderPasswordSection()}
        <h3>Contact information</h3>
        {this.renderNameInput(invalidFields.name)}
        {this.renderPhoneInput(invalidFields.phone)}
        {this.renderAuthModal()}
      </div>
    );
  }
}

export default ContactInfo;



// WEBPACK FOOTER //
// banquet/components/Checkout/ContactInfo/index.js